/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
For adding a new path you can follow the existing paths in the paths array.
1. The `type` key with the `collapse` value is used for a path.
2. The `type` key with the `title` value is used for a title inside the Sidenav.
3. The `type` key with the `divider` value is used for a divider between Sidenav items.
4. The `name` key is used for the name of the path on the Sidenav.
5. The `key` key is used for the key of the path (It will help you with the key prop inside a loop).
6. The `icon` key is used for the icon of the path on the Sidenav, you have to add a node.
7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other paths
inside (nested paths), you need to pass the nested paths inside an array as a value for the `collapse` key.
8. The `path` key is used to store the path location which is used for the react pathr.
9. The `href` key is used to store the external links location.
10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
10. The `//component` key is used to store the //component of its path.
*/
import DashboardHome from "pages/dashboard/DashboardHome";
import NewPatientPage from "pages/dashboard/NewPatientPage";
import PatientsPage from "pages/dashboard/PatientsPage";
import NewProviderPage from "pages/dashboard/NewProviderPage";
import NewAppointmentPage from "pages/dashboard/NewAppointmentPage";
import Login from "pages/Login";
import NewUserPage from "pages/dashboard/NewUserPage";
import ProgressNotePage from "pages/dashboard/ProgressNotePage";
import NewOfficeVisitPage from "pages/dashboard/NewOfficeVisitPage";
import EditPatientPage from "pages/dashboard/EditPatientPage";
import NewDoctorPage from "pages/dashboard/NewDoctorPage";
import DoctorsPage from "pages/dashboard/DoctorsPage";
import EditDoctorPage from "pages/dashboard/EditDoctorPage";
import EditNursePage from "pages/dashboard/EditNursePage";
import { RouteProps } from "react-router-dom";
import UserProfilePage from "pages/dashboard/UserProfilePage";
import TemplatesPage from "pages/dashboard/TemplatePages";
import AppointmentsPage from "pages/dashboard/AppointmentsPage";
import VitalsPage from "pages/dashboard/VitalsPage";
import OfficeVisitPage from "pages/dashboard/OfficeVisitPage";
import PatientHubPage from "pages/dashboard/PatientHubPage";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import PharmacyPage from "pages/dashboard/PharmacyPage";
import FacilityPage from "pages/dashboard/FacilityPage";
import ResourcePage from "pages/dashboard/ResourcePage";
import PrivateRoute from "components/generals/PrivateRoute";
import Unauthorized from "pages/dashboard/Unauthorized";
import { EntityPermissions, PermissionsUser } from "types";
import InsurancePage from "pages/dashboard/InsurancePage";
import BillingEncounterPage from "pages/dashboard/BillingEncounterPage";
import PayerPage from "pages/dashboard/PayerPage";
import PricingPage from "pages/dashboard/PricingPage";
import AccountingsPage from "pages/dashboard/AccountingPage";
import PaymentPage from "pages/dashboard/PaymentsPage";
import AccountingsMovementsPage from "pages/dashboard/AccountingMovementsPage";
import GuestVideochatPage from "components/views/GuestVideochatPage";
import PasswordRecoveryPage from "pages/PasswordRecoveryPage";
import UserPage from "pages/dashboard/UsersPage";
import InsuranceTransactionsPage from "pages/dashboard/InsuranceTransactionPage";
import NursesPage from "pages/dashboard/NursesPage";
import ActivityLogsPage from "pages/dashboard/ActivityLogsPage";
import AssetViewerPage from "pages/dashboard/AssetViewerPage";
import GeneralSettingsPage from "pages/dashboard/GeneralSettingsPage";

/**
 * Extended route type with optional redirect capability.
 * Combines react-router RouteProps with custom redirect field.
 */
export type CustomRouteType = RouteProps & {
  redirect?: string;
};

/**
 * Application route configuration with role-based access control.
 * 
 * Each route includes:
 * - id: Unique identifier for the route
 * - path: URL path from RoutesConstants
 * - element: React component wrapped in PrivateRoute for authorization
 * - redirect: Optional redirect path
 * 
 * Routes are protected using PrivateRoute component with requiredPermissions
 * from the rolePermissions system. Users without required permissions are
 * redirected to the Unauthorized page.
 * 
 * Route Structure:
 * - Authentication: login, password recovery
 * - Dashboard: home, appointments, patient hub
 * - Patient Management: new patient, edit patient, patient list
 * - Provider Management: doctors, nurses, edit provider
 * - Clinical: progress notes, vitals, office visits, templates
 * - Billing: accounting, billing encounters, payments, pricing, payers
 * - Administration: users, facilities, resources, pharmacies, insurance
 * - Telemedicine: guest videochat access
 * - Audit: activity logs
 * 
 * @constant paths
 */
const paths: CustomRouteType[] = [
  {
    id: "appointments",
    path: RoutesConstants.APPOINTMENTS,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW]}>
        <AppointmentsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "dashboard",
    path: RoutesConstants.HOME,
    redirect: RoutesConstants.DASHBOARD,
  },
  {
    id: "dashboard-home",
    path: RoutesConstants.DASHBOARD,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.DASHBOARD].VIEW]}>
        <DashboardHome />
      </PrivateRoute>
    ),
  },
  {
    id: "new-patient",
    path: RoutesConstants.NEW_PATIENT,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NEW_PATIENTS].VIEW]}>
        <NewPatientPage />
      </PrivateRoute>
    ),
  },
  {
    id: "edit-patient",
    path: RoutesConstants.EDIT_PATIENT + ":id",
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.EDIT_PATIENTS].VIEW]}>
        <EditPatientPage />
      </PrivateRoute>
    ),
  },
  {
    id: "patients",
    path: RoutesConstants.PATIENTS,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.PATIENTS].VIEW]}>
        <PatientsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "new-doctor",
    path: RoutesConstants.NEW_DOCTOR,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NEW_DOCTORS].VIEW]}>
        <NewDoctorPage isDoctor={true} />
      </PrivateRoute>
    ),
  },
  {
    id: "new-nurse",
    path: RoutesConstants.NEW_NURSE,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NURSE].VIEW]}>
        <NewDoctorPage isDoctor={false} />
      </PrivateRoute>
    ),
  },
  {
    id: "edit-doctor",
    path: RoutesConstants.EDIT_DOCTOR + ":id",
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.EDIT_DOCTORS].VIEW]}>
        <EditDoctorPage />
      </PrivateRoute>
    ),
  },
  {
    id: "doctors",
    path: RoutesConstants.DOCTORS,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.DOCTORS].VIEW]}>
        <DoctorsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "nurses",
    path: RoutesConstants.NURSES,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NURSE].VIEW]}>
        <NursesPage />
      </PrivateRoute>
    ),
  },
  {
    id: "edit-nurse",
    path: RoutesConstants.EDIT_NURSE + ":id",
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.EDIT_NURSE].VIEW]}>
        <EditNursePage />
      </PrivateRoute>
    ),
  },
  {
    id: "new-provider",
    path: RoutesConstants.NEW_PROVIDER,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW]}>
        <NewProviderPage />
      </PrivateRoute>
    ),
  },
  {
    id: "new-appointment",
    path: RoutesConstants.NEW_APPOINTMENT,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW]}>
        <NewAppointmentPage />
      </PrivateRoute>
    ),
  },
  {
    id: "new-office-visit",
    path: RoutesConstants.NEW_OFFICE_VISIT,
    element: (
      <PrivateRoute
        requiredPermissions={[PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW]}
      >
        <NewOfficeVisitPage />
      </PrivateRoute>
    ),
  },
  {
    id: "login",
    path: RoutesConstants.LOGIN,
    element: <Login />,
  },
  {
    id: "progress-note",
    path: RoutesConstants.PROGRESS_NOTE,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW]}>
        <ProgressNotePage />
      </PrivateRoute>
    ),
  },
  {
    id: "new-user",
    path: RoutesConstants.NEW_USER,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.NEW_USER].VIEW]}>
        <NewUserPage />
      </PrivateRoute>
    ),
  },
  {
    id: "user-profile",
    path: RoutesConstants.USER_PROFILE,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.USER_PROFILE].VIEW]}>
        <UserProfilePage />
      </PrivateRoute>
    ),
  },
  {
    id: "hpis-page",
    path: RoutesConstants.TEMPLATES,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].VIEW]}>
        <TemplatesPage />
      </PrivateRoute>
    ),
  },
  {
    id: "vitals-page",
    path: RoutesConstants.VITALS,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW]}>
        <VitalsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "officeVisit",
    path: RoutesConstants.OFFICE_VISIT,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW]}>
        <OfficeVisitPage />
      </PrivateRoute>
    ),
  },
  {
    id: "patientHub",
    path: RoutesConstants.PATIENT_HUB,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW]}>
        <PatientHubPage />
      </PrivateRoute>
    ),
  },
  {
    id: "pharmacy",
    path: RoutesConstants.PHARMACY,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].VIEW]}>
        <PharmacyPage />
      </PrivateRoute>
    ),
  },
  {
    id: "facility",
    path: RoutesConstants.FACILITY,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].VIEW]}>
        <FacilityPage />
      </PrivateRoute>
    ),
  },
  {
    id: "resource",
    path: RoutesConstants.RESOURCE,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].VIEW]}>
        <ResourcePage />
      </PrivateRoute>
    ),
  },
  {
    id: "unauthorized",
    path: RoutesConstants.UNAUTHORIZED,
    element: <Unauthorized />,
  },
  {
    id: "insurance",
    path: RoutesConstants.INSURANCE,
    element: <InsurancePage />,
  },
  {
    id: "billing-encounter",
    path: RoutesConstants.BILLING_ENCOUNTER,
    element: <BillingEncounterPage />,
  },
  {
    id: "payers",
    path: RoutesConstants.PAYER,
    element: <PayerPage />,
  },
  {
    id: "payments",
    path: RoutesConstants.PAYMENT,
    element: <PaymentPage />,
  },
  {
    id: "pricing",
    path: RoutesConstants.PRICING,
    element: <PricingPage />,
  },
  {
    id: "Accounting",
    path: RoutesConstants.ACCOUNTING,
    element: <AccountingsPage />,
  },
  {
    id: "AccountingMovements",
    path: RoutesConstants.ACCOUNTING_MOVEMENTS,
    element: (
      <PrivateRoute
        requiredPermissions={[PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW]}
      >
        <AccountingsMovementsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "password-recovery",
    path: RoutesConstants.PASSWORD_RECOVERY,
    element: <PasswordRecoveryPage />,
  },
  {
    id: "users",
    path: RoutesConstants.USER,
    element: <UserPage />,
  },
  {
    id: "guest-videochat",
    path: RoutesConstants.VIDEOCHAT_GUEST_CODE,
    element: <GuestVideochatPage />,
  },
  {
    id: "claim",
    path: RoutesConstants.CLAIM,
    element: <InsuranceTransactionsPage />,
  },
  {
    id: "activity-logs",
    path: RoutesConstants.ACTIVITY_LOGS,
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.ACTIVITY_LOGS].VIEW]}>
        <ActivityLogsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "general-settings",
    path: "general-settings",
    element: (
      <PrivateRoute requiredPermissions={[PermissionsUser[EntityPermissions.GENERAL_SETTINGS].VIEW]}>
        <GeneralSettingsPage />
      </PrivateRoute>
    ),
  },
  {
    id: "asset-viewer",
    path: RoutesConstants.ASSET_VIEWER,
    element: <AssetViewerPage />,
  },
];

export default paths;

